import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';
import { useNotify, useGetList } from 'react-admin';
import { apiUpload, PartnerType, PARTNER_TYPE_ROLE_FIELD } from './api';

interface Partner {
  id: string;
  name: string;
  type: string;
}

export const UploadInvoiceDialog = ({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (invoiceId: string) => void;
}) => {
  const [partnerType, setPartnerType] = useState<PartnerType>('transport');
  const [partnerId, setPartnerId] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  // Off by default: multiple files normally means one invoice split across pages
  // (À Votre Service). On means they are separate invoices for one period that
  // should roll up into a single record, with the totals summed.
  const [multiInvoice, setMultiInvoice] = useState(false);
  const [uploading, setUploading] = useState(false);
  const notify = useNotify();

  // Partners come from the existing pgrest resource, filtered to the chosen role.
  const { data: partners, isLoading: loadingPartners } = useGetList<Partner>('partners', {
    filter: { [PARTNER_TYPE_ROLE_FIELD[partnerType]]: true },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'name', order: 'ASC' },
  });

  const partnerOptions = useMemo(() => partners || [], [partners]);

  useEffect(() => {
    // Reset the partner selection whenever the type changes or dialog reopens.
    setPartnerId('');
  }, [partnerType, open]);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setUploading(false);
      setPartnerType('transport');
      setMultiInvoice(false);
    }
  }, [open]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    onDrop: (accepted) => {
      if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    },
  });

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      formData.append('partner_type', partnerType);
      if (partnerId) formData.append('partner_id', partnerId);
      if (multiInvoice) formData.append('multi_invoice', 'true');
      const invoice = await apiUpload('partner-invoices/upload', formData);
      if (invoice.status === 'failed') {
        notify(`Extraction failed: ${invoice.extraction_error || 'unknown error'}`, { type: 'warning' });
      } else {
        notify('Invoice extracted', { type: 'success' });
      }
      onUploaded(invoice.id);
      onClose();
    } catch (err: any) {
      notify(`Upload failed: ${err.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload partner invoice</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          <ToggleButtonGroup
            exclusive
            color="primary"
            value={partnerType}
            onChange={(_, v) => v && setPartnerType(v)}
            fullWidth
          >
            <ToggleButton value="transport">Transport company</ToggleButton>
            <ToggleButton value="crematorium">Crematorium</ToggleButton>
            <ToggleButton value="dropoff_location">Drop-off facility</ToggleButton>
          </ToggleButtonGroup>

          <FormControl fullWidth>
            <InputLabel id="partner-label">Partner (optional — auto-detected)</InputLabel>
            <Select
              labelId="partner-label"
              label="Partner (optional — auto-detected)"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={loadingPartners}
            >
              <MenuItem value="">
                <em>Auto-detect from document</em>
              </MenuItem>
              {partnerOptions.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Drag PDF or Excel invoice files here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add multiple files if one invoice is split across several pages/files
            </Typography>
          </Box>

          {files.length > 1 && (
            <FormControlLabel
              control={<Checkbox checked={multiInvoice} onChange={(e) => setMultiInvoice(e.target.checked)} />}
              label={
                <Box>
                  <Typography variant="body2">These are separate invoices for one period</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rolls them into a single record and sums the totals. Leave off when the files are
                    pages of one invoice.
                  </Typography>
                </Box>
              }
            />
          )}

          {files.length > 0 && (
            <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
              {files.map((f, i) => (
                <ListItem
                  key={`${f.name}-${i}`}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleUpload()}
          disabled={!files.length || uploading}
          startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
        >
          {uploading ? 'Extracting…' : `Upload & extract${files.length > 1 ? ` (${files.length} files)` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
